import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { InputError } from '@/components/common/InputError';
import { EventIcon } from '@/components/insurance/components/FlightFieldIcons';
import { FlightMaskedInput } from '../FlightMaskedInput';
import { useStyles } from './styles';
const MONTHS_AHEAD_TO_SHOW = 12;
const DATE_REGEXP = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.(19|20)\d\d$/;
const DATE_MASK_VALUE = 'ДД.ММ.ГГГГ';
const DATE_MASK_REPLACEMENT = { Д: /\d/, М: /\d/, Г: /\d/ };
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const EMPTY_DAY_KEYS = [
    'empty-1',
    'empty-2',
    'empty-3',
    'empty-4',
    'empty-5',
    'empty-6',
];
const postfixAnimation = {
    variants: {
        hidden: { opacity: 0, scale: 0.3 },
        visible: { opacity: 1, scale: 1 },
    },
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    transition: { duration: 0.08, bounce: 40 },
};
const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const getDayTimestamp = (date) => getStartOfDay(date).getTime();
const getMonthName = (month) => new Date(0, month).toLocaleString('ru', { month: 'long' });
const getFormattedDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
};
const getDateParts = (value) => {
    const [rawDay = '', rawMonth = '', rawYear = ''] = value.split('.');
    const fallbackDigits = value.replace(/\D/g, '').slice(0, 8);
    if (value.includes('.')) {
        return {
            day: rawDay.replace(/\D/g, '').slice(0, 2),
            month: rawMonth.replace(/\D/g, '').slice(0, 2),
            year: rawYear.replace(/\D/g, '').slice(0, 4),
        };
    }
    return {
        day: fallbackDigits.slice(0, 2),
        month: fallbackDigits.slice(2, 4),
        year: fallbackDigits.slice(4, 8),
    };
};
const getMaskedDateDisplayValue = (value) => {
    const { day, month, year } = getDateParts(value);
    const dayDisplay = day ? day.padStart(2, '0') : 'ДД';
    const monthDisplay = month ? month.padStart(2, '0') : 'ММ';
    const yearDisplay = year || 'ГГГГ';
    return `${dayDisplay}.${monthDisplay}.${yearDisplay}`;
};
const getStoredDateValue = (maskedValue) => {
    const { day, month, year } = getDateParts(maskedValue);
    if (!day && !month && !year) {
        return '';
    }
    if (year) {
        return `${day}.${month}.${year}`;
    }
    if (month) {
        return `${day}.${month}`;
    }
    return day;
};
const getDateSegmentFromSelection = (position) => {
    if (position <= 2) {
        return 'day';
    }
    if (position <= 5) {
        return 'month';
    }
    return 'year';
};
const getInputTextWidth = (input, text) => {
    const styles = window.getComputedStyle(input);
    const context = document.createElement('canvas').getContext('2d');
    const letterSpacing = Number.parseFloat(styles.letterSpacing) || 0;
    if (!context) {
        return 0;
    }
    context.font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
    return (context.measureText(text).width +
        Math.max(0, text.length - 1) * letterSpacing);
};
const getDateSegmentFromClickPosition = (input, clientX) => {
    const { left } = input.getBoundingClientRect();
    const displayValue = (input.value || DATE_MASK_VALUE).padEnd(DATE_MASK_VALUE.length, ' ');
    const position = Math.max(0, clientX - left);
    const dayMonthBoundary = (getInputTextWidth(input, displayValue.slice(0, 2)) +
        getInputTextWidth(input, displayValue.slice(0, 3))) /
        2;
    const monthYearBoundary = (getInputTextWidth(input, displayValue.slice(0, 5)) +
        getInputTextWidth(input, displayValue.slice(0, 6))) /
        2;
    if (position < dayMonthBoundary) {
        return 'day';
    }
    return position < monthYearBoundary ? 'month' : 'year';
};
const getDateSegmentRange = (segment) => {
    if (segment === 'day') {
        return { start: 0, end: 2 };
    }
    if (segment === 'month') {
        return { start: 3, end: 5 };
    }
    return { start: 6, end: 10 };
};
const getDateSegmentForInteraction = (requestedSegment, value) => {
    const { day, month } = getDateParts(value);
    if (day.length < 2) {
        return 'day';
    }
    if (requestedSegment === 'year' && month.length < 2) {
        return 'month';
    }
    return requestedSegment;
};
const getNextDateSegment = (segment) => {
    if (segment === 'day') {
        return 'month';
    }
    return 'year';
};
const getDateSegmentMaxValue = (segment) => {
    if (segment === 'day') {
        return 31;
    }
    if (segment === 'month') {
        return 12;
    }
    return 9999;
};
const getDateSegmentMinValue = (segment) => segment === 'year' ? 1000 : 1;
const setDateDisplaySegment = (value, segment, segmentValue) => {
    const nextParts = getDateParts(value);
    nextParts[segment] = segmentValue;
    return getMaskedDateDisplayValue(`${nextParts.day}.${nextParts.month}.${nextParts.year}`);
};
export const parseFlightDate = (value) => {
    if (!DATE_REGEXP.test(value)) {
        return null;
    }
    const [day, month, year] = value.split('.').map(Number);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day) {
        return null;
    }
    return date;
};
const getDateInputValue = (value, isEditing) => {
    if (!value) {
        return '';
    }
    if (isEditing || !parseFlightDate(value)) {
        return getMaskedDateDisplayValue(value);
    }
    return value;
};
const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, 1);
const addCalendarMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
const getMonthsDiff = (from, to) => (to.getFullYear() - from.getFullYear()) * 12 +
    to.getMonth() -
    from.getMonth();
const getDaysInMonth = (year, month) => {
    const date = new Date(year, month, 1);
    const days = [];
    while (date.getMonth() === month) {
        days.push(new Date(date));
        date.setDate(date.getDate() + 1);
    }
    return days;
};
const CheckIcon = () => (_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", height: "24", width: "24", viewBox: "0 -960 960 960", "aria-hidden": "true", children: _jsx("path", { d: "M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" }) }));
const ScrollIcon = () => (_jsx("svg", { width: "28", height: "28", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", children: _jsx("path", { d: "M17 12.3594C17 12.625 16.9062 12.8516 16.6953 13.0547L10.6016 19.0156C10.4297 19.1875 10.2188 19.2734 9.96875 19.2734C9.46094 19.2734 9.05469 18.8828 9.05469 18.3672C9.05469 18.1172 9.16406 17.8906 9.32812 17.7188L14.8281 12.3594L9.32812 7C9.15625 6.82031 9.05469 6.59375 9.05469 6.34375C9.05469 5.83594 9.46094 5.44531 9.96875 5.44531C10.2188 5.44531 10.4297 5.53125 10.6016 5.70312L16.6953 11.6641C16.8984 11.8672 17 12.0938 17 12.3594Z" }) }));
const UnfoldMoreIcon = () => (_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 -960 960 960", height: "20", width: "20", "aria-hidden": "true", children: _jsx("path", { d: "M480-120 300-300l58-58 122 122 122-122 58 58-180 180ZM358-598l-58-58 180-180 180 180-58 58-122-122-122 122Z" }) }));
export const FlightDatePickerInput = memo(({ value, label, placeholder, error, minDate, maxDate, onChange, onBlur, }) => {
    const classes = useStyles();
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const scrollableRef = useRef(null);
    const shouldSelectInitialSegmentRef = useRef(true);
    const [isOpen, setOpen] = useState(false);
    const [isPopoverLayerRaised, setPopoverLayerRaised] = useState(false);
    const [isEditing, setEditing] = useState(false);
    const [displayValue, setDisplayValue] = useState(value);
    const displayValueRef = useRef(displayValue);
    const pendingSegmentInputRef = useRef(null);
    const pointerSegmentRef = useRef(null);
    const pendingSegmentSelectAfterRenderRef = useRef(null);
    const segmentSelectAfterRenderClearTimeoutRef = useRef(null);
    const segmentSelectTimeoutRef = useRef(null);
    const segmentSelectFrameRef = useRef(null);
    const segmentSelectSecondFrameRef = useRef(null);
    const [pressedTimestamp, setPressedTimestamp] = useState(null);
    const today = useMemo(() => getStartOfDay(new Date()), []);
    const maxSelectableDate = useMemo(() => addCalendarMonths(today, MONTHS_AHEAD_TO_SHOW), [today]);
    const normalizedMinDate = useMemo(() => {
        const requestedMinDate = getStartOfDay(minDate || today);
        return requestedMinDate > today ? requestedMinDate : today;
    }, [minDate, today]);
    const normalizedMaxDate = useMemo(() => {
        const requestedMaxDate = maxDate
            ? getStartOfDay(maxDate)
            : maxSelectableDate;
        const limitedMaxDate = requestedMaxDate < maxSelectableDate
            ? requestedMaxDate
            : maxSelectableDate;
        return limitedMaxDate < normalizedMinDate
            ? normalizedMinDate
            : limitedMaxDate;
    }, [maxDate, maxSelectableDate, normalizedMinDate]);
    const selectedDate = useMemo(() => parseFlightDate(value), [value]);
    const lastAvailableMonth = useMemo(() => new Date(normalizedMaxDate.getFullYear(), normalizedMaxDate.getMonth(), 1), [normalizedMaxDate]);
    const firstAvailableMonth = useMemo(() => {
        const minAvailableMonth = new Date(normalizedMinDate.getFullYear(), normalizedMinDate.getMonth(), 1);
        return minAvailableMonth > lastAvailableMonth
            ? lastAvailableMonth
            : minAvailableMonth;
    }, [lastAvailableMonth, normalizedMinDate]);
    const monthsToShow = useMemo(() => Math.max(1, getMonthsDiff(firstAvailableMonth, lastAvailableMonth) + 1), [firstAvailableMonth, lastAvailableMonth]);
    const months = useMemo(() => Array.from({ length: monthsToShow }).map((_, index) => addMonths(firstAvailableMonth, index)), [firstAvailableMonth, monthsToShow]);
    const [visibleMonthIndex, setVisibleMonthIndex] = useState(0);
    const clearQueuedDateSegmentSelect = () => {
        pendingSegmentSelectAfterRenderRef.current = null;
        if (segmentSelectAfterRenderClearTimeoutRef.current !== null) {
            window.clearTimeout(segmentSelectAfterRenderClearTimeoutRef.current);
            segmentSelectAfterRenderClearTimeoutRef.current = null;
        }
        if (segmentSelectTimeoutRef.current !== null) {
            window.clearTimeout(segmentSelectTimeoutRef.current);
            segmentSelectTimeoutRef.current = null;
        }
        if (segmentSelectFrameRef.current !== null) {
            window.cancelAnimationFrame(segmentSelectFrameRef.current);
            segmentSelectFrameRef.current = null;
        }
        if (segmentSelectSecondFrameRef.current !== null) {
            window.cancelAnimationFrame(segmentSelectSecondFrameRef.current);
            segmentSelectSecondFrameRef.current = null;
        }
    };
    const applyDateSegmentSelection = ({ segment, targetInput, shouldRestoreFocus, }) => {
        if (inputRef.current !== targetInput) {
            return;
        }
        if (document.activeElement !== targetInput) {
            if (!shouldRestoreFocus) {
                return;
            }
            targetInput.focus({ preventScroll: true });
        }
        if (document.activeElement !== targetInput) {
            return;
        }
        const { start, end } = getDateSegmentRange(segment);
        targetInput.setSelectionRange(start, end);
    };
    const selectDateSegment = (segment, targetInput = inputRef.current, shouldRestoreFocus = false) => {
        if (!targetInput) {
            return;
        }
        const pendingSegmentSelect = {
            segment,
            targetInput,
            shouldRestoreFocus,
        };
        clearQueuedDateSegmentSelect();
        segmentSelectFrameRef.current = window.requestAnimationFrame(() => {
            segmentSelectFrameRef.current = null;
            applyDateSegmentSelection(pendingSegmentSelect);
            segmentSelectSecondFrameRef.current =
                window.requestAnimationFrame(() => {
                    segmentSelectSecondFrameRef.current = null;
                    applyDateSegmentSelection(pendingSegmentSelect);
                });
        });
    };
    const queueDateSegmentSelect = (segment, targetInput = inputRef.current, shouldRestoreFocus = false) => {
        if (!targetInput) {
            return;
        }
        if (segmentSelectTimeoutRef.current !== null) {
            window.clearTimeout(segmentSelectTimeoutRef.current);
        }
        segmentSelectTimeoutRef.current = window.setTimeout(() => {
            segmentSelectTimeoutRef.current = null;
            selectDateSegment(segment, targetInput, shouldRestoreFocus);
        }, 0);
    };
    const selectDateSegmentAfterRender = (segment, targetInput) => {
        if (segmentSelectAfterRenderClearTimeoutRef.current !== null) {
            window.clearTimeout(segmentSelectAfterRenderClearTimeoutRef.current);
        }
        pendingSegmentSelectAfterRenderRef.current = {
            segment,
            targetInput,
            shouldRestoreFocus: true,
        };
        segmentSelectAfterRenderClearTimeoutRef.current = window.setTimeout(() => {
            pendingSegmentSelectAfterRenderRef.current = null;
            segmentSelectAfterRenderClearTimeoutRef.current = null;
        }, 180);
    };
    const selectDateSegmentFromInput = (input, clientX) => {
        const segment = typeof clientX === 'number'
            ? getDateSegmentFromClickPosition(input, clientX)
            : getDateSegmentFromSelection(input.selectionStart ?? 0);
        return getDateSegmentForInteraction(segment, displayValueRef.current);
    };
    const syncDisplayValue = (nextDisplayValue) => {
        displayValueRef.current = nextDisplayValue;
        setDisplayValue(nextDisplayValue);
        onChange(getStoredDateValue(nextDisplayValue));
    };
    const clearDateSegment = (segment, targetInput) => {
        const nextDisplayValue = setDateDisplaySegment(displayValueRef.current || getMaskedDateDisplayValue(value), segment, '');
        pendingSegmentInputRef.current = null;
        selectDateSegmentAfterRender(segment, targetInput);
        syncDisplayValue(nextDisplayValue);
    };
    const applySmartDateSegmentDigit = (segment, digit, shouldReplaceSegment = false) => {
        const normalizedSegment = getDateSegmentForInteraction(segment, displayValueRef.current || value);
        const pendingSegmentInput = pendingSegmentInputRef.current;
        let nextSegment = normalizedSegment;
        let nextSelectedSegment = normalizedSegment;
        let nextSegmentValue = normalizedSegment === 'year' ? digit : `0${digit}`;
        if (normalizedSegment === 'year') {
            const currentYear = pendingSegmentInput?.segment === 'year' &&
                !shouldReplaceSegment
                ? getDateParts(displayValueRef.current).year
                : '';
            const nextYear = `${currentYear}${digit}`.slice(0, 4);
            pendingSegmentInputRef.current =
                nextYear.length < 4
                    ? { segment: 'year', digit: nextYear }
                    : null;
            const nextDisplayValue = setDateDisplaySegment(displayValueRef.current || getMaskedDateDisplayValue(value), 'year', nextYear);
            syncDisplayValue(nextDisplayValue);
            selectDateSegment('year');
            return;
        }
        if (pendingSegmentInput?.segment === normalizedSegment &&
            !shouldReplaceSegment) {
            const candidateValue = `${pendingSegmentInput.digit}${digit}`;
            const normalizedCandidateValue = Number(candidateValue);
            const canCompleteSegment = normalizedCandidateValue >=
                getDateSegmentMinValue(normalizedSegment) &&
                normalizedCandidateValue <=
                    getDateSegmentMaxValue(normalizedSegment);
            if (canCompleteSegment) {
                nextSegmentValue = candidateValue;
                nextSelectedSegment = getNextDateSegment(normalizedSegment);
                pendingSegmentInputRef.current = null;
            }
            else {
                nextSegment = getNextDateSegment(normalizedSegment);
                nextSelectedSegment = nextSegment;
                nextSegmentValue =
                    nextSegment === 'year' ? digit : `0${digit}`;
                pendingSegmentInputRef.current = {
                    segment: nextSegment,
                    digit,
                };
            }
        }
        else {
            pendingSegmentInputRef.current = {
                segment: normalizedSegment,
                digit,
            };
        }
        const nextDisplayValue = setDateDisplaySegment(displayValueRef.current || getMaskedDateDisplayValue(value), nextSegment, nextSegmentValue);
        syncDisplayValue(nextDisplayValue);
        selectDateSegment(nextSelectedSegment);
    };
    const scrollToMonth = (monthIndex, behavior = 'smooth') => {
        const scrollableElement = scrollableRef.current;
        if (!scrollableElement) {
            return;
        }
        scrollableElement.scrollTo({
            left: scrollableElement.clientWidth * monthIndex,
            behavior,
        });
    };
    const setInitialMonthPosition = (monthIndex) => {
        const scrollableElement = scrollableRef.current;
        if (!scrollableElement) {
            return;
        }
        const previousScrollBehavior = scrollableElement.style.scrollBehavior;
        scrollableElement.style.scrollBehavior = 'auto';
        scrollableElement.scrollLeft =
            scrollableElement.clientWidth * monthIndex;
        window.setTimeout(() => {
            scrollableElement.style.scrollBehavior = previousScrollBehavior;
        }, 0);
    };
    useLayoutEffect(() => {
        const pendingSegmentSelect = pendingSegmentSelectAfterRenderRef.current;
        if (!pendingSegmentSelect) {
            return;
        }
        applyDateSegmentSelection(pendingSegmentSelect);
        if (segmentSelectFrameRef.current !== null) {
            window.cancelAnimationFrame(segmentSelectFrameRef.current);
        }
        if (segmentSelectSecondFrameRef.current !== null) {
            window.cancelAnimationFrame(segmentSelectSecondFrameRef.current);
        }
        segmentSelectFrameRef.current = window.requestAnimationFrame(() => {
            segmentSelectFrameRef.current = null;
            applyDateSegmentSelection(pendingSegmentSelect);
            segmentSelectSecondFrameRef.current =
                window.requestAnimationFrame(() => {
                    segmentSelectSecondFrameRef.current = null;
                    applyDateSegmentSelection(pendingSegmentSelect);
                });
        });
    });
    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }
        const initialDate = selectedDate || normalizedMinDate;
        const initialMonthIndex = Math.max(0, Math.min(months.length - 1, (initialDate.getFullYear() -
            firstAvailableMonth.getFullYear()) *
            12 +
            initialDate.getMonth() -
            firstAvailableMonth.getMonth()));
        setVisibleMonthIndex(initialMonthIndex);
        setInitialMonthPosition(initialMonthIndex);
    }, [
        firstAvailableMonth,
        isOpen,
        months.length,
        normalizedMinDate,
        selectedDate,
    ]);
    useEffect(() => {
        if (isOpen) {
            setPopoverLayerRaised(true);
        }
        if (!isOpen) {
            setPressedTimestamp(null);
        }
    }, [isOpen]);
    useEffect(() => {
        if (!isEditing) {
            displayValueRef.current = value;
            setDisplayValue(value);
        }
    }, [isEditing, value]);
    useEffect(() => {
        const handlePointerDown = (event) => {
            if (rootRef.current &&
                !rootRef.current.contains(event.target)) {
                clearQueuedDateSegmentSelect();
                pendingSegmentInputRef.current = null;
                setEditing(false);
                setOpen(false);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown, true);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            clearQueuedDateSegmentSelect();
        };
    }, []);
    const selectedTimestamp = selectedDate
        ? getDayTimestamp(selectedDate)
        : null;
    const setVisibleMonth = (monthIndex) => {
        const normalizedMonthIndex = Math.max(0, Math.min(months.length - 1, monthIndex));
        setVisibleMonthIndex(normalizedMonthIndex);
        scrollToMonth(normalizedMonthIndex);
    };
    const handleMonthSelect = (event) => {
        setVisibleMonth(Number(event.target.value));
    };
    const handleMonthsScroll = () => {
        const scrollableElement = scrollableRef.current;
        if (!scrollableElement) {
            return;
        }
        const nextMonthIndex = Math.round(scrollableElement.scrollLeft / scrollableElement.clientWidth);
        setVisibleMonthIndex(Math.max(0, Math.min(months.length - 1, nextMonthIndex)));
    };
    const isDateDisabled = (date) => {
        const dayTimestamp = getDayTimestamp(date);
        return (dayTimestamp < getDayTimestamp(normalizedMinDate) ||
            dayTimestamp > getDayTimestamp(normalizedMaxDate));
    };
    const handleDateSelect = (date) => {
        if (isDateDisabled(date)) {
            return;
        }
        const dayTimestamp = getDayTimestamp(date);
        const nextValue = getFormattedDate(date);
        displayValueRef.current = getMaskedDateDisplayValue(nextValue);
        setDisplayValue(displayValueRef.current);
        onChange(nextValue);
        onBlur();
        pendingSegmentInputRef.current = null;
        setEditing(false);
        setOpen(false);
    };
    const handleDayPointerDown = (date) => {
        if (isDateDisabled(date)) {
            return;
        }
        setPressedTimestamp(getDayTimestamp(date));
    };
    const handleDayPointerRelease = () => {
        setPressedTimestamp(null);
    };
    const handleCalendarButtonPointerDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    const handleCalendarButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isOpen) {
            onBlur();
            pendingSegmentInputRef.current = null;
            setEditing(false);
            setOpen(false);
            inputRef.current?.blur();
            return;
        }
        setOpen(true);
    };
    const handleInputFocus = (event) => {
        const nextDisplayValue = getMaskedDateDisplayValue(value);
        pendingSegmentInputRef.current = null;
        displayValueRef.current = nextDisplayValue;
        setEditing(true);
        setDisplayValue(nextDisplayValue);
        if (shouldSelectInitialSegmentRef.current) {
            selectDateSegment(getDateSegmentForInteraction(value ? 'year' : 'day', nextDisplayValue), event.currentTarget);
        }
    };
    const handleInputBlur = () => {
        clearQueuedDateSegmentSelect();
        pendingSegmentInputRef.current = null;
        shouldSelectInitialSegmentRef.current = true;
        setEditing(false);
        onBlur();
    };
    const handleInputPointerDown = (event) => {
        const wasFocused = document.activeElement === event.currentTarget;
        clearQueuedDateSegmentSelect();
        shouldSelectInitialSegmentRef.current = false;
        pointerSegmentRef.current =
            event.pointerType !== 'mouse' || !wasFocused
                ? selectDateSegmentFromInput(event.currentTarget, event.clientX)
                : null;
    };
    const handleInputPointerUp = (event) => {
        pendingSegmentInputRef.current = null;
        if (pointerSegmentRef.current) {
            queueDateSegmentSelect(pointerSegmentRef.current, event.currentTarget);
        }
        shouldSelectInitialSegmentRef.current = true;
    };
    const handleInputClick = (event) => {
        const input = event.currentTarget;
        const segment = pointerSegmentRef.current;
        pendingSegmentInputRef.current = null;
        pointerSegmentRef.current = null;
        if (segment) {
            queueDateSegmentSelect(segment, input);
            return;
        }
        window.setTimeout(() => {
            selectDateSegment(selectDateSegmentFromInput(input));
        }, 0);
    };
    const handleInputChange = (event) => {
        pendingSegmentInputRef.current = null;
        syncDisplayValue(event.target.value);
    };
    const handleInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            setOpen((current) => !current);
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            pendingSegmentInputRef.current = null;
            setEditing(false);
            setOpen(false);
            return;
        }
        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault();
            clearDateSegment(getDateSegmentFromSelection(event.currentTarget.selectionStart ?? 0), event.currentTarget);
            return;
        }
        if (event.key === '.' || event.key === 'ArrowRight') {
            event.preventDefault();
            pendingSegmentInputRef.current = null;
            selectDateSegment(getDateSegmentForInteraction(getDateSegmentFromSelection((event.currentTarget.selectionEnd ?? 0) + 1), displayValueRef.current));
            return;
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            pendingSegmentInputRef.current = null;
            selectDateSegment(getDateSegmentForInteraction(getDateSegmentFromSelection((event.currentTarget.selectionStart ?? 0) - 1), displayValueRef.current));
            return;
        }
        if (/^\d$/.test(event.key)) {
            const segment = getDateSegmentForInteraction(getDateSegmentFromSelection(event.currentTarget.selectionStart ?? 0), displayValueRef.current);
            const { start, end } = getDateSegmentRange(segment);
            const shouldReplaceSegment = event.currentTarget.selectionStart === start &&
                event.currentTarget.selectionEnd === end &&
                pendingSegmentInputRef.current?.segment !== segment;
            event.preventDefault();
            applySmartDateSegmentDigit(segment, event.key, shouldReplaceSegment);
        }
    };
    const inputValue = isEditing
        ? displayValue
        : getDateInputValue(value, isEditing);
    return (_jsxs("div", { className: classes.root, "data-open": isOpen, "data-popover-layer": isPopoverLayerRaised, ref: rootRef, children: [_jsxs("div", { className: classes.inputWrapper, children: [_jsx(FlightMaskedInput, { inputRef: inputRef, value: inputValue, label: label, placeholder: placeholder, mask: DATE_MASK_VALUE, replacement: DATE_MASK_REPLACEMENT, showMask: isEditing, separate: true, invalid: Boolean(error), isActive: isOpen || isEditing, inputMode: "numeric", onFocus: handleInputFocus, onBlur: handleInputBlur, onClick: handleInputClick, onPointerDown: handleInputPointerDown, onPointerUp: handleInputPointerUp, onChange: handleInputChange, onKeyDown: handleInputKeyDown, postfix: _jsx("button", { type: "button", className: classes.calendarButton, "data-open": isOpen, "aria-label": isOpen
                                ? 'Закрыть календарь'
                                : 'Открыть календарь', onPointerDown: handleCalendarButtonPointerDown, onClick: handleCalendarButtonClick, children: _jsx(AnimatePresence, { initial: false, children: !isOpen ? (_createElement(motion.div, { ...postfixAnimation, className: classes.postfixIcon, key: "calendar" },
                                    _jsx(EventIcon, {}))) : (_createElement(motion.div, { ...postfixAnimation, className: classes.postfixConfirm, key: "confirm" },
                                    _jsx(CheckIcon, {}))) }) }) }), _jsx(AnimatePresence, { onExitComplete: () => setPopoverLayerRaised(false), children: isOpen && (_jsxs(motion.div, { className: classes.popover, initial: { opacity: 0, y: -6, scale: 0.98 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: -6, scale: 0.98 }, transition: { duration: 0.16 }, children: [_jsx("div", { className: classes.staticScrollButtons, children: _jsxs(AnimatePresence, { initial: false, children: [visibleMonthIndex > 0 && (_jsx(motion.div, { className: classes.scrollButtonWrapper, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: {
                                                    opacity: 0,
                                                    transition: {
                                                        duration: 0.05,
                                                    },
                                                }, children: _jsx("button", { type: "button", className: classes.scrollButton, "data-direction": "left", onClick: () => setVisibleMonth(visibleMonthIndex -
                                                        1), "aria-label": "\u041F\u0440\u0435\u0434\u044B\u0434\u0443\u0449\u0438\u0439 \u043C\u0435\u0441\u044F\u0446", tabIndex: -1, children: _jsx("div", { children: _jsx(ScrollIcon, {}) }) }) }, "prev-month")), visibleMonthIndex <
                                                months.length - 1 && (_jsx(motion.div, { className: classes.scrollButtonWrapper, "data-right": "true", initial: { opacity: 0 }, animate: { opacity: 1 }, exit: {
                                                    opacity: 0,
                                                    transition: {
                                                        duration: 0.05,
                                                    },
                                                }, children: _jsx("button", { type: "button", className: classes.scrollButton, "data-direction": "right", onClick: () => setVisibleMonth(visibleMonthIndex +
                                                        1), "aria-label": "\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0438\u0439 \u043C\u0435\u0441\u044F\u0446", tabIndex: -1, children: _jsx("div", { children: _jsx(ScrollIcon, {}) }) }) }, "next-month"))] }) }), _jsx("div", { className: classes.weekdays, children: WEEKDAYS.map((day) => (_jsx("div", { className: classes.weekday, children: day }, day))) }), _jsx("div", { className: classes.monthViewport, ref: scrollableRef, tabIndex: -1, onScroll: handleMonthsScroll, children: _jsx("div", { className: classes.monthes, children: months.map((month, index) => {
                                            const days = getDaysInMonth(month.getFullYear(), month.getMonth());
                                            const firstDayOffset = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() +
                                                6) %
                                                7;
                                            return (_jsxs("div", { className: classes.month, children: [_jsx("div", { className: classes.monthHeaderWrapper, children: _jsxs("div", { className: classes.monthHeader, children: [_jsxs("button", { type: "button", className: classes.monthHeaderTitle, tabIndex: -1, children: [_jsxs("span", { children: [getMonthName(month.getMonth()), ' ', month.getFullYear()] }), _jsx(UnfoldMoreIcon, {})] }), _jsx("select", { value: index, onChange: handleMonthSelect, "aria-label": "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u043C\u0435\u0441\u044F\u0446", children: months.map((optionMonth, optionIndex) => (_jsxs("option", { value: String(optionIndex), children: [getMonthName(optionMonth.getMonth()), ' ', optionMonth.getFullYear()] }, `${optionMonth.getFullYear()}-${optionMonth.getMonth()}`))) })] }) }), _jsx("div", { className: classes.weekdaysSpacer }), _jsxs("div", { className: classes.days, children: [EMPTY_DAY_KEYS.slice(0, firstDayOffset).map((emptyDayKey) => (_jsx("div", { className: classes.emptyDay }, emptyDayKey))), days.map((day) => {
                                                                const dayTimestamp = getDayTimestamp(day);
                                                                const isDisabled = isDateDisabled(day);
                                                                const isPressed = !isDisabled &&
                                                                    pressedTimestamp ===
                                                                        dayTimestamp;
                                                                const isSelected = !isDisabled &&
                                                                    selectedTimestamp ===
                                                                        dayTimestamp;
                                                                const isToday = dayTimestamp ===
                                                                    getDayTimestamp(new Date());
                                                                return (_jsxs("button", { type: "button", className: classes.day, disabled: isDisabled, "data-disabled": isDisabled, "data-selected": isSelected, "data-pressed": isPressed, onPointerDown: () => handleDayPointerDown(day), onPointerUp: handleDayPointerRelease, onPointerCancel: handleDayPointerRelease, onPointerLeave: handleDayPointerRelease, onClick: () => handleDateSelect(day), children: [_jsx("span", { children: day.getDate() }), isToday && (_jsx("i", { className: classes.todayMarker }))] }, dayTimestamp));
                                                            })] })] }, `${month.getFullYear()}-${month.getMonth()}`));
                                        }) }) })] })) })] }), _jsx(InputError, { error: error })] }));
});
